/**
 * A small collapsible panel.
 *
 * Secondary material - the game log, the how-to-play card, a lesson's long
 * explanation - used to occupy permanent dashboard space next to the table.
 * Here it folds away behind its own title instead, using a native <details>
 * so it stays keyboard reachable and screen-reader friendly with no script.
 *
 * Open state is React state rather than the bare `open` attribute, so a
 * re-render (a bot moving, the language changing) never snaps a drawer shut
 * under the player's hands.
 */

import { useState } from "react";
import type { ReactNode } from "react";

export interface DrawerProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** Short right-aligned note in the title row, e.g. a count. */
  note?: string;
  testId?: string;
}

export function Drawer({ title, children, defaultOpen, className, note, testId }: DrawerProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <details
      className={`drawer ${className ?? ""}`.trim()}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      {...(testId ? { "data-testid": testId } : {})}
    >
      <summary className="drawer__summary">
        <span className="drawer__title">{title}</span>
        {note ? <span className="drawer__note">{note}</span> : null}
        <span className="drawer__chevron" aria-hidden="true" />
      </summary>
      <div className="drawer__body">{children}</div>
    </details>
  );
}
